import { createBrowserHistory, type History, type To } from '@remix-run/router';

/**
 * The dashboard lives at /<slug>/admin/… — the business's own address, like its register at /<slug>/pos. Pages keep
 * linking to /admin/…: this browser history writes the signed-in business's slug in front of those paths when the app
 * navigates and when it renders links, so no page needs to know the slug. With no slug (signed out, or a business
 * without one) /admin/… stays as it is, and old /admin/… links keep working (AppShell moves them to the slug).
 */
let slug: string | null = null;

/** The signed-in business (its slug from the token or the organization), or null when signed out. */
export const setBusinessSlug = (value: string | null | undefined) => { slug = value || null; };
export const businessSlug = () => slug;

const ADMIN = /^\/admin(?=[/?#]|$)/;

/** /admin/clients → /cafebolero/admin/clients for the signed-in business; anything else unchanged. */
export const withBusiness = (path: string) => (slug && ADMIN.test(path) ? path.replace(ADMIN, `/${slug}/admin`) : path);

/** /cafebolero/admin/clients → /admin/clients: the page, whatever the business (to tell which section is open). */
export const withoutBusiness = (pathname: string) => pathname.replace(/^\/[^/]+(?=\/admin(?:\/|$))/, '');

const rewrite = (to: To): To =>
  typeof to === 'string' ? withBusiness(to) : { ...to, ...(to.pathname ? { pathname: withBusiness(to.pathname) } : {}) };

const browser = createBrowserHistory({ v5Compat: true });

export const businessHistory: History = {
  get action() { return browser.action; },
  get location() { return browser.location; },
  createHref: to => browser.createHref(rewrite(to)),
  createURL: to => browser.createURL(rewrite(to)),
  encodeLocation: to => browser.encodeLocation(rewrite(to)),
  push: (to, state) => browser.push(rewrite(to), state),
  replace: (to, state) => browser.replace(rewrite(to), state),
  go: delta => browser.go(delta),
  listen: listener => browser.listen(listener),
};
